import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { useCompany } from '../../app/providers/CompanyProvider';
export function HeaderCompanySelector() {
  const {
    selectedCompany,
    companies,
    setSelectedCompany
  } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close on Escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);
  const selectCompany = (company: typeof selectedCompany) => {
    setSelectedCompany(company);
    setIsOpen(false);
  };
  return <div className="relative" ref={dropdownRef} data-id="element-14723">
      <button onClick={() => setIsOpen(!isOpen)} aria-label={`Selected company: ${selectedCompany.name}`} aria-expanded={isOpen} aria-haspopup="listbox" className="flex items-center justify-between w-full sm:min-w-48 sm:w-auto max-w-[160px] sm:max-w-64 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" data-id="element-14724">
        <div className="flex items-center min-w-0 flex-1" data-id="element-14725">
          {selectedCompany.logo ? <img src={selectedCompany.logo} alt="" className="w-4 h-4 rounded-full mr-2 flex-shrink-0" data-id="element-14726" /> : <BuildingIcon size={14} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-14727" />}
          <span className="truncate text-xs" data-id="element-14728">{selectedCompany.name}</span>
        </div>
        <ChevronDownIcon size={14} className={`ml-2 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-14729" />
      </button>

      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-14730" />

          <div role="listbox" aria-label="Select company or sub merchant" className="absolute left-0 z-20 w-[280px] sm:w-80 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden" data-id="element-14731">
            {/* Master merchant section */}
            <div className="border-b border-gray-200" data-id="element-14732">
              <div className="px-3 py-2 bg-gray-50" data-id="element-14733">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-14734">
                  Master Merchant
                </span>
              </div>
              {companies.filter(c => c.type === 'master').map(company => {
            const isSelected = selectedCompany.id === company.id;
            return <div key={company.id} role="option" aria-selected={isSelected} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-14735">
                      <div className="flex items-center flex-1 min-w-0" data-id="element-14736">
                        {company.logo ? <img src={company.logo} alt="" className="w-6 h-6 rounded-full mr-3 flex-shrink-0" data-id="element-14737" /> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-14738">
                            <span className="text-xs font-medium text-gray-600" data-id="element-14739">
                              {company.name.charAt(0)}
                            </span>
                          </div>}
                        <div className="min-w-0 flex-1" data-id="element-14740">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-14741">
                            {company.name}
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0" data-id="element-14742">
                        {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-14743" />}
                      </div>
                    </div>;
          })}
            </div>

            {/* Sub Merchants section */}
            <div data-id="element-14744">
              <div className="px-3 py-2 bg-gray-50" data-id="element-14745">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-14746">
                  Sub Merchants
                </span>
              </div>
              <div className="max-h-64 overflow-y-auto" data-id="element-14747">
                {companies.filter(c => c.type === 'submerchant').map(company => {
              const isSelected = selectedCompany.id === company.id;
              return <div key={company.id} role="option" aria-selected={isSelected} onClick={() => selectCompany(company)} className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-14748">
                        <div className="flex items-center flex-1 min-w-0" data-id="element-14749">
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-14750">
                            <BuildingIcon size={12} className="text-gray-500" data-id="element-14751" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-14752">
                            <div className="text-sm text-gray-900 truncate" data-id="element-14753">
                              {company.name}
                            </div>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0" data-id="element-14754">
                          {isSelected && <CheckIcon size={16} className="text-blue-600" data-id="element-14755" />}
                        </div>
                      </div>;
            })}
              </div>
            </div>
          </div>
        </>}
    </div>;
}